import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PayrollSnackRewardItem } from '../../../services/payroll-snacks-assignment/payroll-snacks-assignment.service';

export interface EditTeamSnacksDialogData {
  teamName: string;
  rewards: PayrollSnackRewardItem[];
  selectedSnackRewardIds: string[];
}

export interface EditTeamSnacksDialogResult {
  snackRewardIds: string[];
}

@Component({
  selector: 'app-edit-team-snacks-dialog',
  templateUrl: './edit-team-snacks-dialog.component.html',
  styleUrls: ['./edit-team-snacks-dialog.component.scss'],
})
export class EditTeamSnacksDialogComponent {
  public rewards: PayrollSnackRewardItem[];
  public previewRewardId = '';
  private customSnackRewardIds: string[];

  constructor(
    public dialogRef: MatDialogRef<EditTeamSnacksDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditTeamSnacksDialogData,
  ) {
    this.rewards = this.normalizeRewards(data?.rewards || []);
    this.customSnackRewardIds = this.normalizeSelectedIds(data?.selectedSnackRewardIds || []);
  }

  isSelected(rewardId: string): boolean {
    return this.customSnackRewardIds.includes(rewardId);
  }

  toggleReward(rewardId: string, checked: boolean): void {
    const selectedIds = new Set(this.customSnackRewardIds);
    if (checked) {
      selectedIds.add(rewardId);
    } else {
      selectedIds.delete(rewardId);
    }
    this.customSnackRewardIds = this.sortSelectedIds(Array.from(selectedIds));
  }

  getSelectedCount(): number {
    return this.customSnackRewardIds.length;
  }

  clearSelected(): void {
    this.customSnackRewardIds = [];
  }

  previewReward(reward: PayrollSnackRewardItem, event?: Event): void {
    event?.stopPropagation();
    const rewardId = reward?.id || '';
    this.previewRewardId = this.previewRewardId === rewardId ? '' : rewardId;
  }

  getPreviewReward(): PayrollSnackRewardItem | undefined {
    if (!this.previewRewardId) {
      return undefined;
    }
    return this.rewards.find((reward) => reward.id === this.previewRewardId);
  }

  isPreviewed(rewardId: string): boolean {
    return !!rewardId && this.getPreviewReward()?.id === rewardId;
  }

  trackReward(_index: number, reward: PayrollSnackRewardItem): string {
    return reward?.id;
  }

  okClicked(): void {
    this.dialogRef.close({
      snackRewardIds: [...this.customSnackRewardIds],
    });
  }

  onDismiss(): void {
    this.dialogRef.close(undefined);
  }

  private normalizeRewards(rewards: PayrollSnackRewardItem[]): PayrollSnackRewardItem[] {
    return (Array.isArray(rewards) ? rewards : [])
      .filter((reward) => !!`${reward?.id || ''}`.trim())
      .map((reward) => ({
        ...reward,
        id: `${reward.id || ''}`.trim(),
        title: `${reward.title || reward.id || ''}`.trim(),
        subtitle: `${reward.subtitle || ''}`.trim(),
        imageUrl: `${reward.imageUrl || ''}`.trim(),
      }))
      .sort((left, right) => left.title.localeCompare(right.title));
  }

  private normalizeSelectedIds(values: string[]): string[] {
    const availableIds = new Set(this.rewards.map((reward) => reward.id));
    return this.sortSelectedIds(Array.from(new Set(
      (Array.isArray(values) ? values : [])
        .map((value) => `${value || ''}`.trim())
        .filter((value) => !!value && availableIds.has(value))
    )));
  }

  private sortSelectedIds(values: string[]): string[] {
    const selected = new Set(values);
    return this.rewards
      .map((reward) => reward.id)
      .filter((rewardId) => selected.has(rewardId));
  }
}
